/**
 * Client error reporter.
 *
 * The single always-on error telemetry path for the web app. Window errors,
 * unhandled rejections, and React-boundary catches are batched and POSTed to
 * /api/client-errors (edge-api), which stores them in api_error_log alongside
 * server errors (method 'CLIENT') with the same prune + spike-alerting.
 *
 * Design constraints:
 * - Never throw, never recurse: every failure here is swallowed.
 * - Bounded: max reports per session, max repeats per unique message, so a
 *   crash loop cannot flood the backend (it also rate-limits per IP).
 * - Suppression layers elsewhere must never run BEFORE these listeners are
 *   installed; this module registers first (see index.jsx) so it sees every
 *   event even when a later capture handler stops propagation.
 */

const MAX_REPORTS_PER_SESSION = 20;
const MAX_REPEATS_PER_MESSAGE = 3;
const FLUSH_AT = 5;
const FLUSH_INTERVAL_MS = 4000;

const state = {
  installed: false,
  queue: [],
  sentCount: 0,
  messageCounts: new Map(),
  timer: null,
};

function normalizeReason(reason) {
  if (!reason) return null;
  if (typeof reason === 'string') {
    return { message: reason.slice(0, 500), stack: null };
  }
  const message = reason.message || String(reason);
  if (!message) return null;
  return {
    message: message.slice(0, 500),
    stack: typeof reason.stack === 'string' ? reason.stack.slice(0, 4000) : null,
  };
}

// Known-benign noise that carries no signal (browser quirks, expected OAuth
// callback flows). Chunk-load failures are NOT on this list: they are the
// strongest bad-deploy signal there is.
function isBenign(message) {
  return (
    message.includes('ResizeObserver') ||
    message.includes('code verifier') ||
    message.includes('code_verifier')
  );
}

export function flush() {
  if (!state.queue.length) return;
  const batch = state.queue.splice(0, state.queue.length);
  state.sentCount += batch.length;
  try {
    fetch('/api/client-errors', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      body: JSON.stringify({
        reports: batch.map((entry) => ({
          ...entry,
          url: (typeof window !== 'undefined' && window.location.href) || null,
        })),
      }),
    }).catch(() => {});
  } catch (_) {
    // Reporting must never break the app.
  }
}

export function reportClientError(error, { source = 'manual', componentStack = null } = {}) {
  try {
    if (state.sentCount >= MAX_REPORTS_PER_SESSION) return;
    const normalized = normalizeReason(error);
    if (!normalized || isBenign(normalized.message)) return;
    const seen = state.messageCounts.get(normalized.message) || 0;
    if (seen >= MAX_REPEATS_PER_MESSAGE) return;
    state.messageCounts.set(normalized.message, seen + 1);
    state.queue.push({
      message: normalized.message,
      stack: normalized.stack,
      source,
      componentStack: componentStack ? String(componentStack).slice(0, 2000) : null,
      ts: Date.now(),
    });
    if (state.queue.length >= FLUSH_AT) {
      flush();
      return;
    }
    if (!state.timer) {
      state.timer = setTimeout(() => {
        state.timer = null;
        flush();
      }, FLUSH_INTERVAL_MS);
    }
  } catch (_) {
    // Reporting must never break the app.
  }
}

export function installClientErrorReporter() {
  if (state.installed || typeof window === 'undefined') return;
  state.installed = true;

  // Capture phase: registered before any suppression guards in index.jsx, so
  // an event that a later guard stops still gets reported here.
  window.addEventListener(
    'error',
    (event) => {
      reportClientError(event?.error || event?.message, { source: 'window_error' });
    },
    true,
  );
  window.addEventListener(
    'unhandledrejection',
    (event) => {
      reportClientError(event?.reason, { source: 'unhandled_rejection' });
    },
    true,
  );
  window.addEventListener('pagehide', flush);
}
